/**
 * Is er op deze cel HANDMATIG ingegrepen? — puur een OPZOEKING, geen berekening.
 *
 * Een cel die de teamleider zelf heeft aangepast ziet er in de matrix identiek uit als een
 * cel die de engine heeft gepland. Bij het opschonen van september waren de eigen correcties
 * achteraf niet terug te vinden (§42q). Het wijzigingslogboek weet het wél: elke aanroep van
 * wijzigCel schrijft een record met soort 'cel' (useRoosterStore.js:477-486, via
 * meldWijziging). Die records komen al mee naar de client — GET /api/data geeft het hele
 * omgeving-blok terug (server/src/index.js:214), `wijzigingen` staat daarin omdat de PUT hem
 * beschermt (index.js:302-304), en serverSync.js:84 spreidt het antwoord in de store.
 *
 * Er wordt hier dus NIETS bijgehouden, NIETS gemuteerd en GEEN nieuw dataveld gemaakt —
 * alleen teruggelezen wat er al staat. Zelfde afspraak als ruilmarkering.js:10-14: signaleren,
 * niet nabouwen.
 *
 * ⚠️ NIET via vindPlaatsing (Weekrooster.jsx) te gebruiken. Die zoekt een naam ÍN het
 * rooster; na een 'weghalen' staat de naam daar juist niet meer. De opzoeking hieronder joint
 * daarom op het adres UIT HET RECORD, niet op wat er nu in het rooster staat.
 */

// Een cel-record wijst pas een echte cel aan als het ALLE zes velden draagt. Geen terugval op
// een ontbrekend veld: half matchen zet een teken op de verkeerde cel, en dat is erger dan
// geen teken — de teamleider zou dan een ingreep "zien" die daar niet ligt. Zelfde regel en
// zelfde reden als slotCompleet in ruilmarkering.js:25-31.
const ADRES_VELDEN = ['isoJaar', 'weeknummer', 'dag', 'gemeenteKey', 'dienstId', 'wieNaam'];

function adresCompleet(w) {
  return !!w && typeof w === 'object'
    && ADRES_VELDEN.every((veld) => w[veld] !== undefined && w[veld] !== null && w[veld] !== '');
}

/**
 * DE NETTO-REGEL — waarom saldo en niet "het laatste record wint".
 *
 * Per slot (isoJaar, weeknummer, dag, gemeenteKey, dienstId, wieNaam) tellen we
 *     saldo = aantal 'toevoegen' − aantal 'weghalen'
 * en er komt alleen een teken als saldo ≠ 0:
 *     saldo > 0  → deze persoon is hier met de hand NEERGEZET
 *     saldo < 0  → deze persoon is hier met de hand WEGGEHAALD
 *     saldo = 0  → er is aan gezeten, maar de cel staat weer precies zoals de engine hem
 *                  plande → GEEN teken
 *
 * "Het laatste record wint" zou hetzelfde antwoord geven zolang de records netjes AFWISSELEN,
 * en dat doen ze: wijzigCel meldt alleen als er echt iets verandert (useRoosterStore.js:471
 * geeft {} terug bij een no-op), dus twee keer 'toevoegen' zonder 'weghalen' ertussen kan niet
 * ontstaan. Bij strikte afwisseling is saldo altijd −1, 0 of +1, en is saldo = 0 precies
 * hetzelfde als "eerste en laatste operatie verschillen".
 *
 * Toch saldo, om twee redenen. Ten eerste zegt het teken dan wat er NETTO is gebeurd in plaats
 * van wat er als laatste is geklikt — dat is de vraag die de teamleider stelt. Ten tweede
 * blijft het antwoord zinnig als de afwisseling ooit tóch breekt: het logboek kan gaten hebben
 * (wijzigingSync.js:56-63 zet een mislukte POST NIET terug, en de ring-cap van 2000 snoeit op
 * tijdstip — server/src/index.js:1079-1083). "Het laatste record wint" zou bij een gat een
 * willekeurige richting aanwijzen; saldo geeft dan hooguit een teken te veel.
 *
 * ⚠️ WAT DIT NIET KAN: een gat detecteren. Raakt de 'toevoegen' van een toevoegen/weghalen-paar
 * kwijt, dan blijft er een losse 'weghalen' staan en verschijnt er een teken op een cel die in
 * werkelijkheid ongewijzigd is. Dat is niet op te lossen zonder de records die er niet zijn.
 *
 * GEMETEN OP DE ECHTE DATA (server/dev-data/backup.json, 11 aug 2026): 11 cel-records op
 * 9 unieke cellen, waarvan 2 een toevoegen→weghalen-paar met saldo 0, en 0 cellen met
 * meerdere records in dezelfde richting. Zeven slots houden dus een saldo ≠ 0 over — maar
 * daarna komt de toets hieronder er nog overheen.
 */

/**
 * DE TWEEDE TOETS: KLOPT HET SALDO NOG MET DE HUIDIGE CEL?
 *
 * Een record zegt alleen dat er OOIT geklikt is. Het zegt niets over of die klik er nu nog
 * toe doet. Een herstel via Beheer > Importeer back-up vervangt maandroosters in z'n geheel
 * ZONDER meldWijziging (importBackup in useRoosterStore.js), en opnieuw genereren doet hetzelfde:
 * de plaatsing verdwijnt, het record blijft staan. Zonder deze toets wijst het teken dan naar
 * een ingreep die is teruggedraaid — precies wat er live gebeurde op Bram, week 33 maandag:
 * een stip "handmatig neergezet" op een cel die leeg is, omdat het herstel van 5 augustus
 * 17:15 de plaatsing terugdraaide terwijl het record van 17:18 bleef staan.
 *
 *   saldo > 0 (neergezet)  → teken alleen als de persoon NU in dat slot staat
 *   saldo < 0 (weggehaald) → teken alleen als de persoon NU NIET in dat slot staat
 *
 * ⚠️ "In dat slot", niet "die dag ergens". De toets leest rooster[gemeenteKey][dienstId][dag]
 * met het adres UIT HET RECORD. Danny is in week 32 op dinsdag uit arnhem/late_dienst_dw
 * weggehaald; dat hij die dag elders wél ergens staat mag zijn teken niet wegnemen, en
 * andersom mag een gelijknamige plaatsing in een ander slot er geen teken van maken.
 *
 * DE TOETS ZIT IN DEZE FUNCTIE EN NIET IN Weekrooster.jsx, om één reden: `aantal` telt de
 * slots die netto iets veranderd hebben, en een slot dat deze toets niet haalt hoort daar niet
 * in mee te tellen. Die telling zit in de lus hieronder; hem buiten de functie herhalen zou
 * betekenen dat de regel op twee plekken staat. De functie blijft puur — hij krijgt een kaal
 * rooster-object mee, geen React en geen store.
 */
function saldoVan(records) {
  return records.reduce((s, w) => s + (w.operatie === 'toevoegen' ? 1 : -1), 0);
}

/**
 * Oplopende vergelijker op tijdstip, met `id` als HARDE tie-break.
 *
 * Alleen op tijdstip vergelijken is niet genoeg: records uit één POST krijgen exact hetzelfde
 * tijdstip. De server stempelt per record een eigen `new Date().toISOString()`
 * (server/src/index.js:1201), maar een batch wordt binnen dezelfde milliseconde verwerkt, en
 * de debounce in wijzigingSync (1500 ms) zorgt er juist voor dat samenhangende bewerkingen in
 * één batch belanden. Gemeten: de twee records van een verplaatsing droegen allebei
 * 2026-08-11T13:24:50.522Z.
 *
 * Zonder tweede sleutel besliste de invoegvolgorde van de Map wie er "jongst" was, en die is
 * een gevolg van de array-volgorde in de opslag — niet iets waar dit bestand op mag leunen.
 * Het teken kon daardoor bij gelijke tijdstippen omslaan van 'neergezet' naar 'weggehaald'
 * zonder dat er iets veranderd was. `id` is uniek per record (index.js:1056) en verandert
 * nooit meer, dus hij maakt de uitkomst reproduceerbaar. Welk id "groter" is heeft geen
 * betekenis — het gaat er alleen om dat dezelfde invoer altijd hetzelfde antwoord geeft.
 */
function vergelijkRecords(a, b) {
  return String(a?.tijdstip).localeCompare(String(b?.tijdstip))
    || String(a?.id).localeCompare(String(b?.id));
}

/**
 * @param {Array}  wijzigingen  store.wijzigingen — komt vers van de server (serverSync.js:84).
 *                              Heeft GEEN startwaarde in de store, dus vóór de eerste
 *                              laadVanServer is dit undefined. Zie de guard hieronder.
 * @param {object} rooster      week.rooster van de week die de matrix TOONT — de tweede toets
 *                              hierboven leest hieruit. Ontbreekt hij, dan valt er niets te
 *                              toetsen en komt er GEEN teken: een teken dat niet tegen de
 *                              huidige stand gehouden kan worden is precies het probleem dat
 *                              deze toets oplost.
 * @param {number} isoJaar
 * @param {number} weeknummer
 * @param {string} dag          dagcode 'ma'..'zo'
 * @param {string} medewerker   de naam in de matrixrij — matcht op `wieNaam` van het record
 * @returns {{operatie:'toevoegen'|'weghalen', doorNaam:string, tijdstip:string,
 *            gemeenteKey:string, dienstId:string, aantal:number}|null}
 *   null = op deze cel is netto niets met de hand gedaan.
 *
 *   `doorNaam` is de TEAMLEIDER die de wijziging deed (record.naam, uit het token gestempeld —
 *   server/src/index.js:1058). Bewust NIET `wieNaam`: dat is de medewerker in de cel, en die
 *   staat al in de rij waar het teken op komt. De twee verwisselen zou de verkeerde persoon
 *   aanwijzen.
 *
 *   `operatie` is 'toevoegen', 'weghalen' of 'verplaatst'. Die derde is er omdat ÉÉN klik in
 *   het celmenu TWEE wijzigCel-aanroepen doet: weghalen van het oude slot en toevoegen op het
 *   nieuwe (Weekrooster.jsx:259-260). Dat is één handeling, geen twee.
 *   Bij 'verplaatst' dragen `van` en `naar` de twee slots ({ gemeenteKey, dienstId }).
 *
 *   `aantal` = op hoeveel verschillende diensten van deze persoon op deze dag netto iets is
 *   veranderd — NA de toets tegen de huidige stand. Hier stond eerder dat meer dan 1 betekent
 *   dat iemand "op twee diensten is bijgeplaatst of weggehaald"; dat was onjuist en het
 *   veroorzaakte een tooltip die bij een doodgewone verplaatsing beweerde dat er meer dan één
 *   dienst was aangepast. Een verplaatsing IS twee slots (aantal = 2) maar één handeling, en
 *   wordt daarom apart herkend. Pas vanaf drie slots is er echt meer dan één handeling.
 *   De teruggegeven doorNaam/tijdstip horen bij het MEEST RECENTE record — zelfde keuze als
 *   ruilOpCel, die bij meerdere wissels ook één treffer teruggeeft met een `aantal` ernaast
 *   (ruilmarkering.js:60-66).
 */
export function handmatigOpCel(wijzigingen, rooster, isoJaar, weeknummer, dag, medewerker) {
  if (!Array.isArray(wijzigingen) || wijzigingen.length === 0) return null;
  if (!rooster || typeof rooster !== 'object') return null;

  // Groepeer per slot: het saldo geldt per (gemeente, dienst), niet per matrixcel. Eén
  // matrixcel is (persoon, dag) en kan dus meer dan één slot raken.
  const perSlot = new Map();
  for (const w of wijzigingen) {
    // Alleen soort 'cel'. 'divergentie'-records dragen HETZELFDE cel-adres (de server vult dat
    // voor allebei — server/src/index.js:1054) en zouden zonder deze regel meematchen, terwijl
    // ze niets zeggen over een handmatige ingreep: die worden juist door de server zelf
    // geschreven bij een consistentie-afwijking.
    if (!w || w.soort !== 'cel') continue;
    // Zonder operatie valt er niets te salderen; zo'n record telt niet mee.
    if (w.operatie !== 'toevoegen' && w.operatie !== 'weghalen') continue;
    if (!adresCompleet(w)) continue;
    if (w.isoJaar !== isoJaar || w.weeknummer !== weeknummer) continue;
    if (w.dag !== dag || w.wieNaam !== medewerker) continue;

    const sleutel = `${w.gemeenteKey}|${w.dienstId}`;
    if (!perSlot.has(sleutel)) perSlot.set(sleutel, []);
    perSlot.get(sleutel).push(w);
  }
  if (perSlot.size === 0) return null;

  // Eerst alle slots verzamelen die netto iets veranderd hebben ÉN de toets tegen de huidige
  // stand halen. Pas daarna beslissen wat het teken zegt: of dit een verplaatsing is, is een
  // eigenschap van de VERZAMELING slots en niet van één slot afzonderlijk.
  const overgebleven = [];
  for (const records of perSlot.values()) {
    const saldo = saldoVan(records);
    if (saldo === 0) continue; // per saldo terug bij af → dit slot geeft geen teken

    // Tweede toets: klopt het saldo nog met wat er NU in dit slot staat? Zie de toelichting
    // hierboven. Het adres komt uit het record (gemeenteKey/dienstId/dag), niet uit de
    // huidige plaatsing van deze persoon — "staat hij die dag ergens" is de verkeerde vraag.
    const { gemeenteKey, dienstId } = records[0];
    const namenInSlot = rooster?.[gemeenteKey]?.[dienstId]?.[dag];
    const staatErNu = Array.isArray(namenInSlot) && namenInSlot.includes(medewerker);
    // neergezet maar staat er niet meer, of weggehaald maar staat er weer → verouderd record.
    if (saldo > 0 ? !staatErNu : staatErNu) continue;

    // Het meest recente record van dit slot draagt wie het deed en wanneer. Sorteren op
    // tijdstip en niet op array-volgorde vertrouwen: de opslag staat nieuwste-eerst
    // (server/src/index.js:1204), maar dat is een eigenschap van de opslag en geen belofte
    // aan deze functie.
    const laatste = records
      .slice()
      .sort((a, b) => vergelijkRecords(a, b))
      .pop();

    overgebleven.push({ saldo, gemeenteKey, dienstId, laatste });
  }
  if (overgebleven.length === 0) return null;

  const aantal = overgebleven.length;
  // Het jongste record over alle slots draagt doorNaam en tijdstip. Zelfde vergelijker als
  // hierboven, dus ook hier is de uitkomst niet afhankelijk van de Map-volgorde.
  const jongste = overgebleven
    .map((s) => s.laatste)
    .slice()
    .sort((a, b) => vergelijkRecords(a, b))
    .pop();
  const basis = { doorNaam: jongste.naam, tijdstip: jongste.tijdstip, aantal };

  // EEN VERPLAATSING IS ÉÉN HANDELING. Precies twee slots, één +1 en één −1: dat is wat het
  // celmenu doet bij het kiezen van een andere dienst (Weekrooster.jsx:259-260, eerst
  // weghalen dan toevoegen). Zonder deze tak leverde dat `aantal: 2` op en daarmee de zin
  // "op deze dag is meer dan één dienst aangepast", terwijl er één keer geklikt is.
  if (overgebleven.length === 2) {
    const naar = overgebleven.find((s) => s.saldo > 0);
    const van = overgebleven.find((s) => s.saldo < 0);
    if (naar && van) {
      return {
        ...basis,
        operatie: 'verplaatst',
        van: { gemeenteKey: van.gemeenteKey, dienstId: van.dienstId },
        naar: { gemeenteKey: naar.gemeenteKey, dienstId: naar.dienstId },
        // Het adres van het teken is waar de persoon NU staat.
        gemeenteKey: naar.gemeenteKey,
        dienstId: naar.dienstId,
      };
    }
    // Twee slots dezelfde kant op (twee keer toegevoegd, of twee keer weggehaald) is geen
    // verplaatsing; die valt hieronder in de gewone tak met aantal = 2.
  }

  // Eén slot, of drie of meer: het jongste slot bepaalt wat er staat.
  const winnaar = overgebleven
    .slice()
    .sort((a, b) => vergelijkRecords(a.laatste, b.laatste))
    .pop();
  return {
    ...basis,
    operatie: winnaar.saldo > 0 ? 'toevoegen' : 'weghalen',
    gemeenteKey: winnaar.gemeenteKey,
    dienstId: winnaar.dienstId,
  };
}
